'use client';

/**
 * Client-side receiver for server → client `workspace.widgets.*`
 * tool RPCs.
 *
 * Architecture (the long version lives in
 * `docs/adr/0015-widget-command-bus.md`):
 *
 *   1. Server-side: the agent runtime calls a `workspace.widgets.*`
 *      tool. The portal skill (`lib/agents/portal-workspace-widgets-skill.ts`)
 *      forwards it to `requestWidgetCommand` which publishes an
 *      `agent.command` frame on `workspace.bus` with a `requestId`
 *      and a `targetTabId`.
 *
 *   2. Every workspace member's WebSocket fans the frame to every
 *      open tab (via `useWorkspaceBus`). Only the tab whose stable
 *      `useWorkspaceTabId()` matches `targetTabId` proceeds — every
 *      other tab silently drops the frame so we don't double-execute
 *      Python kernels / xterm sessions / etc.
 *
 *   3. The selected tab runs
 *      `dispatchWorkspaceWidgetsRequest(bus, request, ctx)` against
 *      its local `WidgetCommandBus`. Errors are normalised to
 *      `{ ok: false, error: { code, message } }`.
 *
 *   4. The receiver sends a `workspace.bus.reply` event back through
 *      the existing UAMP WebSocket (`UAMPBrowserClient.sendWorkspaceBusReply`).
 *      The server matches the reply by `requestId` and resolves the
 *      agent's pending tool call.
 *
 * Lifecycle:
 *   - Mounted once at workspace shell scope, alongside the
 *     `WidgetCommandBusProvider`. It is a non-visual component.
 *   - Subscriptions tear down on workspace unmount.
 *
 * Streaming (`subscribe`):
 *   - `dispatchWorkspaceWidgetsRequest` returns a `handle` with a
 *     `.close()` callback. We park the handle on a per-requestId Map
 *     so the next `subscribe` to the SAME requestId acts as an
 *     unsubscribe — the agent runtime's `signal.abort()` path closes
 *     the parent subscription server-side; the corresponding fan-out
 *     frame surfaces here with a `close: true` flag (TODO future).
 *
 *     For now, push frames from a `subscribe` invoke are sent as
 *     individual `workspace.bus.reply` payloads with the SAME
 *     requestId — the server's pending entry isn't resolved by them
 *     (resolve only happens on the initial subscribe response with
 *     the handle's stream id). This component owns the lifetime of
 *     each subscribe handle and tears them all down on unmount.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useUAMPContext } from '@/components/uamp-provider';
import { useWidgetCommandBus } from '@/lib/workspaces/widget-command-bus-context';
import { useWorkspaceBus } from '@/lib/workspaces/use-workspace-bus';
import { useWorkspaceTabId } from '@/lib/workspaces/use-workspace-tab-id';
import {
  useCurrentScreenId,
  useWorkspaceActions,
  useWorkspaceItems,
} from '@/components/workspace/store';
import { entryOfItem, orderScreenEntries } from '@/lib/workspaces/screens';
import { addLocalWidgetGrant } from '@/lib/workspaces/widget-permissions-local';
import { dispatchWorkspaceWidgetsRequest } from '@/lib/workspaces/widget-command-bus-dispatch';
import type { WorkspaceWidgetsRequest } from '@/lib/agents/workspace-widgets-skill';
import type { WorkspaceItemDTO } from '@/lib/workspaces/types';
import { WorkspaceWidgetsError } from '@/lib/agents/workspace-widgets-skill';

interface AgentCommandFrame {
  requestId: string;
  targetTabId: string | null;
  request: WorkspaceWidgetsRequest;
  agentId: string | null;
  userId: string | null;
  actor?: { kind?: string; id?: string; name?: string; avatar?: string } | null;
  timeoutMs?: number;
}

/** How long an inbound invoke waits for its target widget to REGISTER on
 *  the bus before dispatching anyway. A command can land while the widget
 *  iframe is still booting — e.g. an MCP invoke fired right after a `/w/`
 *  share view (or a canvas) loads — and without the grace it answers
 *  "No widget registered" for a widget that appears a second later. Well
 *  under the 30s default command timeout, so a genuinely-wrong id still
 *  errors promptly (the post-grace dispatch produces the normal error). */
const REGISTRATION_GRACE_MS = 10_000;

/**
 * Resolve `true` as soon as `itemId` is registered on the bus, `false`
 * after `timeoutMs`. Resolves immediately when already registered.
 * Exported for tests.
 */
export function waitForWidgetRegistration(
  // Structural minimum of `WidgetCommandBus` so tests can pass a tiny fake.
  bus: {
    describe(itemId: string): unknown;
    subscribeList(cb: () => void): () => void;
  },
  itemId: string,
  timeoutMs: number,
): Promise<boolean> {
  if (bus.describe(itemId)) return Promise.resolve(true);
  return new Promise((resolve) => {
    let done = false;
    let off: () => void = () => {};
    let timer: ReturnType<typeof setTimeout> | undefined;
    const finish = (found: boolean) => {
      if (done) return;
      done = true;
      off();
      if (timer !== undefined) clearTimeout(timer);
      resolve(found);
    };
    off = bus.subscribeList(() => {
      if (bus.describe(itemId)) finish(true);
    });
    timer = setTimeout(() => finish(false), timeoutMs);
    // Re-check after subscribing: a registration that landed between the
    // initial probe and the subscription would otherwise be missed until
    // the next list change (or the timeout).
    if (bus.describe(itemId)) finish(true);
  });
}

/**
 * True for a bus permission denial, in EITHER spelling it can reach us in.
 *
 * The bus throws `BusError('permission')`, but `dispatchWorkspaceWidgetsRequest`
 * re-wraps it as `WorkspaceWidgetsError('workspace-widgets/permission')` on the
 * way out. A check for the bare code alone silently disabled the consent
 * prompt: every denial looked unrecognised, so the agent got a dead-end error
 * and the owner was never asked. Exported so a test pins both spellings.
 */
export function isAgentPermissionDenial(code: string): boolean {
  return code === 'permission' || code === 'workspace-widgets/permission';
}

/*
 * WHAT A DENIED AGENT IS TOLD, IN WORDS IT CAN REPEAT (the e2e visual pass of
 * 2026-10-06, scenarios RV-B1, RV-B2, RV-C10 and RX-11). The bus's own
 * denial is the evaluator's, "Widget does not allow this agent to control
 * it" (lib/agents/visibility.ts), and this receiver used to APPEND to it
 * ("... was requested from the person using it, stop and tell them to look
 * for the approval prompt"). The model then told the person "activate the
 * widget" and "grant me access to the Revent widget", and pointed at "the
 * approval prompt", which a person looking at a hidden tab only ever sees
 * as a row in Notifications. So on a canvas the whole message is replaced:
 * app words only, the place the person approves named (their
 * notifications), and no word a paraphrase could turn into "widget". A
 * shared link keeps the bus's words, which are already plain and carry the
 * person's own answer (widget-operate-consent.tsx). Agent-facing strings:
 * they go to the model's tool result and are never drawn for a person.
 */
/* THE AGENT, NEVER THE PERSON, IS THE ONE NOT YET ALLOWED (the dev visual
   pass of 2026-10-09, finding 4: the model repeated "You are not allowed to
   work in this app yet" to the person, who read it as being about them).
   Each line now tells the agent what to SAY, in the first person, and the
   request it points to is raised again at the top of the bell (the
   permission-request route), so "the request that just came up" is true. */
/** The base refusal on a canvas, when no request could be raised. */
export const AGENT_CONTROL_DENIED =
  'You may not work in this app for the person yet. Say so in the first person ("I can\'t work in this app for you yet") and never that THEY are not allowed. Do not try again on your own.';
/** The person was asked: their notifications hold the request, at the top. */
export const AGENT_CONTROL_ASKED =
  'You may not work in this app for the person yet. A request to allow you has just been raised, at the top of their notifications. ' +
  'Tell them in the first person, in one plain sentence: you can\'t work in this app for them yet, they can allow it in the request that just came up in their notifications, then ask you again. ' +
  'Never say that THEY are not allowed. Do not try again until they do.';
/** No request could be raised (a failed request, or no app to name). */
export const AGENT_CONTROL_NOT_ASKED =
  'You may not work in this app for the person yet. The person can let you in from the app\'s settings, under Agent control. ' +
  'Tell them in the first person, in one plain sentence ("I can\'t work in this app for you yet"), then wait. Never say that THEY are not allowed. Do not try again until they do.';

/**
 * Ask the app's owner to let this agent drive it. Rides the SAME endpoint and
 * the same critical-notification consent screen as `host.requestPermission`,
 * so "an app wants your library" and "an agent wants this app" look and behave
 * alike. Best-effort: a failure here just means the agent gets the plain
 * denial message. Returns whether the request was raised.
 *
 * Share surfaces (`share-<contentId>`) have no workspace item to grant on, so
 * there is nothing to ask for and we skip.
 */
async function requestAgentControl(args: {
  workspaceId: string;
  itemId: string;
  agentId: string;
  agentName: string | null;
  widgetName: string | null;
}): Promise<'asked' | 'granted' | false> {
  if (args.workspaceId.startsWith('share-')) return false;
  try {
    const res = await fetch(
      `/api/workspaces/${encodeURIComponent(args.workspaceId)}/items/${encodeURIComponent(args.itemId)}/permission-request`,
      {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          agent: { id: args.agentId, name: args.agentName ?? '' },
          widgetName: args.widgetName ?? undefined,
        }),
      },
    );
    if (!res.ok) return false;
    /* THE YES ALREADY GIVEN (the review of 2026-10-08): for the person's own
       app agent on the app's own window with the per-app consent on record,
       the route grants instead of asking and says so; the caller appends
       the grant and runs the command once more, so no one is told to look
       for a question that was never raised. */
    const body = (await res.json().catch(() => null)) as { granted?: unknown } | null;
    return body?.granted === true ? 'granted' : 'asked';
  } catch {
    return false;
  }
}

function isAgentCommandPayload(
  payload: unknown,
): payload is AgentCommandFrame {
  if (!payload || typeof payload !== 'object') return false;
  const p = payload as Record<string, unknown>;
  return (
    typeof p.requestId === 'string' &&
    typeof p.request === 'object' &&
    p.request !== null &&
    typeof (p.request as { tool?: unknown }).tool === 'string'
  );
}

export interface WorkspaceAgentCommandReceiverProps {
  workspaceId: string;
  /**
   * Presence heartbeat endpoint override. Defaults to the workspace
   * presence route; the standalone share viewer (`/w/`) passes its
   * content-scoped route (`/api/content/:id/presence`) because its
   * `workspaceId` is the synthetic `share-<contentId>` surface id,
   * which no `/api/workspaces/:id/*` route accepts. Everything else —
   * bus subscription, dispatch, reply uplink — is identical across
   * both surfaces.
   */
  presenceEndpoint?: string;
}

export function WorkspaceAgentCommandReceiver({
  workspaceId,
  presenceEndpoint,
}: WorkspaceAgentCommandReceiverProps) {
  const bus = useWidgetCommandBus();
  const tabId = useWorkspaceTabId();
  const { client } = useUAMPContext();
  const { mergeRemoteItem, patchItemState, addItem } = useWorkspaceActions();
  // Live items, held in a ref so the grant replay below can read the CURRENT
  // allow-lists without re-running whenever any item changes (a drag rewrites
  // x/y on every frame).
  const items = useWorkspaceItems();
  const itemsRef = useRef(items);
  itemsRef.current = items;
  // The screen the person is parked on, in a ref for the same reason: an
  // agent open reads it to know where the app is expected to land, and must
  // read the value at COMMAND time, not the one captured when the handler was
  // created. Changes rarely (a screen switch), so subscribing costs nothing.
  const currentScreenId = useCurrentScreenId();
  const currentScreenIdRef = useRef(currentScreenId);
  currentScreenIdRef.current = currentScreenId;
  // Identity of the item SET, not its contents: the grant replay must re-run
  // when the canvas populates or an item appears, but not on every drag frame
  // (x/y live in item state and rewrite constantly).
  const itemIdsKey = useMemo(
    () => items.map((it) => it.id).sort().join(','),
    [items],
  );
  const pendingGrantsRef = useRef<Array<{ itemId: string; agentId: string; ifUnset?: boolean }>>([]);
  const appliedGrantsRef = useRef<Set<string>>(new Set());
  const [grantsLoadedAt, setGrantsLoadedAt] = useState(0);

  // Render items created out-of-band — an agent via MCP `widget_instantiate`,
  // or one of the user's other tabs — without a page refresh. Idempotent by
  // id, so an echo of our own optimistic add is a no-op.
  useWorkspaceBus({ workspaceId, kinds: ['item.created'] }, (frame) => {
    if (frame.payload) mergeRemoteItem(frame.payload as WorkspaceItemDTO);
  });

  // Apply per-widget permission grants live. When the user approves a
  // `widget_permission_request`, the server emits `workspace.permission_granted`
  // (its `updateItem` is a no-op now — item state is Yjs-owned), so THIS
  // client is what must persist the grant: we PATCH `state.permissions` into
  // the workspace Y.Doc (NOT persist:false). That both re-keys + reloads the
  // iframe with the new Permissions-Policy `allow=` AND makes the grant
  // durable across reloads — otherwise the widget re-asks every load.
  useEffect(() => {
    if (!client) return;
    const off = client.on('uamp:event', (event: unknown) => {
      const ev = event as {
        type?: string;
        workspaceId?: string;
        itemId?: string;
        feature?: unknown;
        permissions?: unknown;
      };
      if (ev?.type !== 'workspace.permission_granted') return;
      if (ev.workspaceId !== workspaceId) return;
      if (typeof ev.itemId !== 'string') return;
      if (typeof ev.feature !== 'string' || !ev.feature) return;

      // SECURITY: persist the grant PER-USER, PER-DEVICE (localStorage) —
      // NEVER the shared Y.Doc. The doc is writable by every Editor, so a
      // shared grant would hand this widget `allow=microphone` in OTHER
      // users' browsers (silent capture for anyone who already trusts the
      // portal with that device). The store subscribes to this change and
      // re-projects → the one iframe re-keys with the new Permissions-Policy
      // `allow=`, and the grant survives reloads for THIS user only.
      addLocalWidgetGrant(workspaceId, ev.itemId, ev.feature);

      // (Agent-control approvals arrive as `workspace.agent_control_granted`
      // and are handled in the effect below — a different write target.)

      // D6: a grant only works if the PORTAL origin can supply the
      // feature. For device APIs (camera / mic / geo) re-acquire the
      // browser site-permission for the portal before the iframe
      // reloads; Permissions-Policy-only features (xr / sensors) are
      // a no-op here (delegation header alone suffices).
      void import('@/lib/workspaces/permission-reconcile')
        .then(({ reconcilePortalCapability }) =>
          reconcilePortalCapability(ev.feature as string),
        )
        .then((res) => {
          if (res.status === 'denied' && res.guidance) {
            // Best-effort guidance — the hard browser block can't be
            // overridden programmatically.
            console.warn(`[widget-permission] ${res.guidance}`);
          }
        })
        .catch(() => {
          /* reconcile is best-effort */
        });
    });
    return off;
  }, [client, workspaceId]);

  // Apply an approved AGENT-CONTROL grant. The owner tapped "Allow" on the
  // consent screen the receiver raised when the bus denied this agent; the
  // server marked the notification read and emitted this event, and THIS client
  // performs the authoritative write, because canvas item state is Yjs-owned.
  //
  // Unlike device features — which are per-user, per-device localStorage grants
  // so an editor can't arm a widget in someone else's browser — agent control
  // goes in the SHARED doc. It is a property of the app, not of a viewer's
  // device: the agent's command lands in whichever tab answers it, so a grant
  // that only existed on the approver's machine would be a coin flip.
  // Append, never replace: approving one agent must not revoke another. Read
  // the CURRENT rules off the bus registration rather than a captured snapshot
  // so two approvals in quick succession don't clobber each other. Shared by
  // the approval event, the route's "already granted" answer and the retry.
  const appendGrant = useCallback(
    (itemId: string, agentId: string) => {
      const current = bus?.itemSnapshot(itemId)?.state?.agentControl;
      const allow = Array.isArray(current) ? current : current?.allow ?? [];
      if (allow.includes(agentId) || allow.includes('everyone') || allow.includes('*')) return;
      patchItemState(itemId, { agentControl: { allow: [...allow, agentId] } });
    },
    [bus, patchItemState],
  );
  // A NO IS WRITTEN ON THE WINDOW (the review of 2026-10-08): the owner's
  // deny on the consent screen takes the agent off this item's list and
  // leaves `agentControl` SET (an empty list is a decision), so the own-app
  // replay leaves the window alone at the next mount.
  const removeGrant = useCallback(
    (itemId: string, agentId: string) => {
      const current = bus?.itemSnapshot(itemId)?.state?.agentControl;
      const allow = Array.isArray(current) ? current : current?.allow ?? [];
      patchItemState(itemId, { agentControl: { allow: allow.filter((a) => a !== agentId) } });
    },
    [bus, patchItemState],
  );
  useEffect(() => {
    if (!client) return;
    const off = client.on('uamp:event', (event: unknown) => {
      const ev = event as {
        type?: string;
        workspaceId?: string;
        itemId?: string;
        agentId?: string;
      };
      if (ev?.type !== 'workspace.agent_control_granted' && ev?.type !== 'workspace.agent_control_denied') return;
      if (ev.workspaceId !== workspaceId) return;
      if (typeof ev.itemId !== 'string' || typeof ev.agentId !== 'string') return;
      if (!ev.itemId || !ev.agentId) return;
      if (ev.type === 'workspace.agent_control_denied') removeGrant(ev.itemId, ev.agentId);
      else appendGrant(ev.itemId, ev.agentId);
    });
    return off;
  }, [client, workspaceId, appendGrant, removeGrant]);

  // Replay approved-but-unapplied agent-control grants.
  //
  // The grant write above only runs if a tab is live when the user taps Allow.
  // Approving from the notification bell with the workspace closed used to be a
  // silent no-op: success reported, prompt consumed, nothing written. The
  // decision is durable server-side (`actionTaken: 'approved'`), so on mount we
  // ask for any grants this workspace has not applied yet and append them.
  // Idempotent — already-present grants are skipped, and the write is a CRDT
  // append so two tabs mounting together converge.
  // Step 1 — FETCH the decisions once per workspace.
  useEffect(() => {
    if (!bus || workspaceId.startsWith('share-')) return;
    let cancelled = false;
    pendingGrantsRef.current = [];
    appliedGrantsRef.current = new Set();
    void (async () => {
      try {
        const res = await fetch(
          `/api/workspaces/${encodeURIComponent(workspaceId)}/agent-grants`,
          { credentials: 'same-origin' },
        );
        if (!res.ok || cancelled) return;
        const { grants } = (await res.json()) as {
          grants?: Array<{ itemId: string; agentId: string; ifUnset?: boolean }>;
        };
        if (cancelled || !grants?.length) return;
        pendingGrantsRef.current = grants;
        // Nudge step 2 now that there is something to apply.
        setGrantsLoadedAt(Date.now());
      } catch {
        // Best-effort: never block the canvas on replay.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bus, workspaceId]);

  // Step 2 — APPLY them once the items they target actually exist.
  //
  // Splitting fetch from apply is the whole point. A single mount-time pass
  // read the store before Yjs had synced, found no matching item, skipped every
  // grant and never ran again — so the replay silently did nothing, which is
  // the very failure it was written to fix. Keying this on the item-id set
  // means it re-runs the moment the canvas populates.
  useEffect(() => {
    if (!pendingGrantsRef.current.length) return;
    const remaining: Array<{ itemId: string; agentId: string }> = [];
    for (const g of pendingGrantsRef.current) {
      const key = `${g.itemId}:${g.agentId}`;
      if (appliedGrantsRef.current.has(key)) continue;
      // Read the allow-list from the STORE, not the bus: at mount the widget
      // may not have registered, so `bus.itemSnapshot` would report an empty
      // list and applying this grant would REPLACE an existing grant for a
      // different agent instead of appending to it.
      const item = itemsRef.current.find((it) => it.id === g.itemId);
      if (!item) {
        // Not synced YET — keep it and retry on the next item change rather
        // than dropping it.
        remaining.push(g);
        continue;
      }
      appliedGrantsRef.current.add(key);
      const current = item.state?.agentControl;
      // A derived own-app grant (lib/workspaces/own-app-agent-grants.ts) is
      // for a window nobody has configured. The route read a projection that
      // lags the document; the LIVE item decides, so a No given a moment ago
      // is never written over (the review of 2026-10-08).
      if (g.ifUnset && current !== undefined && current !== null) continue;
      const allow = Array.isArray(current) ? current : current?.allow ?? [];
      if (allow.includes(g.agentId) || allow.includes('everyone') || allow.includes('*')) {
        continue;
      }
      patchItemState(g.itemId, { agentControl: { allow: [...allow, g.agentId] } });
    }
    pendingGrantsRef.current = remaining;
  }, [itemIdsKey, grantsLoadedAt, patchItemState]);

  // ── Tab presence heartbeat ──────────────────────────────────────────
  // Publish this live tab to the presence registry so a context-free
  // caller — Claude over the portal `/mcp` endpoint, via
  // `lib/mcp/workspace-widget-tools.ts` — can discover the tab plus the
  // widgets it can drive. The in-workspace agent path does NOT need this:
  // it gets `targetTabId` straight from the chat context. Best-effort
  // throughout; the short Redis TTL reaps a tab that stops pinging.
  useEffect(() => {
    if (!bus || !tabId) return;
    let stopped = false;
    let inflight = false;
    let lastKey = '';
    const url =
      presenceEndpoint ??
      `/api/workspaces/${encodeURIComponent(workspaceId)}/presence`;
    // `bus.subscribeList` fires on ANY registration change — including a
    // widget's `updateItem` on every state change, so a DRAG (x/y is state)
    // fires it per frame. The registry only cares WHICH widgets are open, not
    // where, so dedupe on the sorted open-item-id set: a move (ids unchanged)
    // is a no-op; mount/unmount re-pings. `force` bypasses the dedupe for the
    // interval + refocus so the Redis TTL stays fresh even when the set is
    // stable. Single-flight (+ a 10s abort) so a slow/down backend can't stack
    // requests. No `keepalive`: that's a small-quota UNLOAD beacon, not a live
    // heartbeat — under a slow backend it turned this into an
    // ERR_INSUFFICIENT_RESOURCES flood.
    // The periodic tick + fetch live in a dedicated WORKER when possible
    // (pipe-clean P1-1): widgets run in same-process sandboxed iframes, so a
    // widget's long synchronous task freezes the WHOLE tab's event loop — a
    // main-thread interval stops ticking, the 90s TTL reaps the tab, and a
    // merely-busy tab reads as CLOSED to workspace_list_open. A worker thread
    // keeps re-posting the last-known payload straight through such a stall.
    // Main thread stays the source of truth for WHAT to post (open set,
    // visibility) and falls back to the old inline interval when workers are
    // unavailable (CSP without worker-src blob:, ancient browsers).
    const WORKER_SRC =
      "let url=null,payload=null,inflight=false;" +
      "function post(){if(!url||!payload||inflight)return;inflight=true;" +
      "const ac=new AbortController();const to=setTimeout(function(){ac.abort()},10000);" +
      "fetch(url,{method:'POST',headers:{'content-type':'application/json'},body:JSON.stringify(payload),credentials:'same-origin',signal:ac.signal})" +
      ".catch(function(){}).finally(function(){clearTimeout(to);inflight=false})}" +
      "setInterval(post,15000);" +
      "onmessage=function(e){var d=e&&e.data?e.data:{};if(d.url)url=d.url;if(d.payload)payload=d.payload;if(d.now)post()};";
    let worker: Worker | null = null;
    let workerUrl: string | null = null;
    try {
      workerUrl = URL.createObjectURL(
        new Blob([WORKER_SRC], { type: 'application/javascript' }),
      );
      worker = new Worker(workerUrl);
    } catch {
      worker = null; // fall back to the inline interval below
    }
    const ping = (force: boolean) => {
      if (stopped) return;
      const openItemIds = bus.list().map((w) => w.itemId).sort();
      const key = openItemIds.join(',');
      if (!force && key === lastKey) return;
      lastKey = key;
      const body = {
        tabId,
        openItemIds,
        // Hidden/occluded tabs have rAF + timers frozen — the invoke ranker
        // must prefer a visible tab or canvas/compute commands hang there.
        visible: document.visibilityState === 'visible',
      };
      if (worker) {
        // Absolute URL: a blob worker's base is the blob, not the page.
        worker.postMessage({
          url: new URL(url, window.location.origin).toString(),
          payload: body,
          now: true,
        });
        return;
      }
      if (inflight) return;
      inflight = true;
      const ac = new AbortController();
      const to = setTimeout(() => ac.abort(), 10_000);
      void fetch(url, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
        signal: ac.signal,
      })
        .catch(() => {
          /* presence is a hint, not a source of truth */
        })
        .finally(() => {
          clearTimeout(to);
          inflight = false;
        });
    };
    ping(true);
    // Worker owns the periodic re-post; the inline interval is only the
    // no-worker fallback (and a frozen main thread stops it — the exact bug).
    const iv = worker ? null : setInterval(() => ping(true), 15_000);
    // Re-ping when the OPEN SET changes (widget mount/unmount) so invoke
    // targeting stays fresh between intervals — but not on mere moves/state.
    const offList = bus.subscribeList(() => ping(false));
    // A backgrounded tab throttles the interval past the presence TTL; the
    // moment it's refocused (e.g. the user switches back to drive it),
    // re-ping right away so it reappears in presence without a gap. Ping on
    // BOTH transitions — going hidden must also reach the registry promptly,
    // or the invoke ranker keeps routing to a tab that just froze its timers.
    const onVis = () => ping(true);
    document.addEventListener('visibilitychange', onVis);
    const clear = () => {
      try {
        navigator.sendBeacon(
          url,
          new Blob([JSON.stringify({ tabId, clear: true })], {
            type: 'application/json',
          }),
        );
      } catch {
        /* best-effort */
      }
    };
    window.addEventListener('pagehide', clear);
    return () => {
      stopped = true;
      if (iv !== null) clearInterval(iv);
      if (worker) worker.terminate();
      if (workerUrl) URL.revokeObjectURL(workerUrl);
      offList();
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pagehide', clear);
      clear();
    };
  }, [bus, tabId, workspaceId, presenceEndpoint]);

  // Track live subscribe handles so we can close them on unmount or
  // when the workspace changes. Keyed by requestId because that's
  // the only stable identifier the server hands us back.
  const subscribeHandlesRef = useRef<Map<string, { close: () => void }>>(
    new Map(),
  );

  useWorkspaceBus(
    { workspaceId, kinds: ['agent.command'] },
    (frame) => {
      // Hot-path filters first — every workspace member's tab gets
      // the same fan-out, so the vast majority of frames are not
      // for this tab and we want the no-op to be a single string
      // comparison.
      if (!isAgentCommandPayload(frame.payload)) return;
      const cmd = frame.payload;
      if (!tabId) return;
      if (cmd.targetTabId !== tabId) return;
      if (!bus || !client) {
        // Best-effort error reply so the agent doesn't time out
        // when the receiver isn't fully wired (rare — usually a
        // race during workspace mount).
        client?.sendWorkspaceBusReply({
          workspaceId,
          requestId: cmd.requestId,
          ok: false,
          error: {
            code: 'workspace-widgets/no-bus',
            message: 'No live command bus on this tab',
          },
        });
        return;
      }

      void (async () => {
        try {
          // Host-level mount (the hosted `open_app` tool, 2026-09-05): open
          // an app on THIS tab's canvas. Deliberately `addItem` with no
          // x/y, so the store's placement chooses the spot, plus
          // `openedBy: 'agent'`: since 2026-09-29 (the Stage algorithm,
          // rule 8) the window lands on top of the screen the person is
          // CURRENTLY on, like their own open, with the spawn highlight and
          // without taking the keyboard (see `openedBy` in the store).
          // The server already verified the caller can read the ref and that
          // it is an app row; the frame reaches only the caller's own tabs
          // (`requestWidgetCommand` scopes by userId), so the add runs under
          // the person's own session, as their own click would.
          if (cmd.request.tool === 'workspace_open_app') {
            const { refId, cache } = cmd.request;
            // Which screen the person is on, read BEFORE the mount: mounting
            // focuses the new window, and focusing moves `currentScreenId`
            // onto whatever screen the window ended up on.
            const screenBefore = entryOfItem(
              orderScreenEntries(itemsRef.current),
              currentScreenIdRef.current,
            );
            const item = await addItem({
              type: 'content',
              refId,
              openedBy: 'agent',
              state: cache ? ({ cache } as never) : undefined,
            });
            // CHECK where it landed rather than assuming: an agent that tells
            // the person "it is on your screen" when it is not sends them
            // looking for something that is not there. Since 2026-09-29 the
            // store always opens on the person's screen (rules 2 to 8), so
            // `new-screen` should no longer occur; the check stays because
            // the answer is the agent's word to the person. Only ever claims
            // `new-screen` when the window provably left the screen the
            // person was on; with no screen resolved at all there was none
            // to leave, and the window went where the camera was.
            const cx = item.state.x + item.state.width / 2;
            const cy = item.state.y + item.state.height / 2;
            const rect = screenBefore?.rect;
            const leftTheScreen =
              !!rect &&
              !(
                cx >= rect.x &&
                cx <= rect.x + rect.width &&
                cy >= rect.y &&
                cy <= rect.y + rect.height
              );
            client.sendWorkspaceBusReply({
              workspaceId,
              requestId: cmd.requestId,
              ok: true,
              result: {
                itemId: item.id,
                workspaceId,
                refId,
                placedOn: leftTheScreen ? 'new-screen' : 'current-screen',
              },
            });
            return;
          }

          // Registration grace: an invoke/subscribe that arrives while the
          // target widget's iframe is still booting (MCP command fired right
          // after a page load) would hit "No widget registered" — wait
          // briefly for the registration instead of failing a widget that
          // appears a second later. Never applied to `logs`: logs are
          // buffered host-side and matter most when the widget FAILED to
          // register, so waiting there is pure added latency. A wrong id
          // pays the grace once, then errors normally on dispatch.
          if (
            (cmd.request.tool === 'workspace_widgets_invoke' ||
              cmd.request.tool === 'workspace_widgets_subscribe') &&
            cmd.request.itemId
          ) {
            await waitForWidgetRegistration(
              bus,
              cmd.request.itemId,
              // Stay well inside the command's own timeout so the caller
              // gets a real error rather than a bus RPC timeout.
              Math.min(
                REGISTRATION_GRACE_MS,
                Math.max((cmd.timeoutMs ?? 30_000) - 5_000, 1_000),
              ),
            );
          }

          // For `subscribe`, push frames flow back through the
          // streamPush callback. We forward each as an additional
          // `workspace.bus.reply` frame with the SAME requestId so
          // the server-side tool-delta channel can emit them. The
          // server treats only the FIRST reply (carrying `ok` +
          // `result` for the handle) as resolving the pending
          // Promise; subsequent frames are stream payloads.
          const streamPush = (push: {
            itemId: string;
            eventName: string;
            payload: unknown;
          }) => {
            client.sendWorkspaceBusReply({
              workspaceId,
              requestId: cmd.requestId,
              ok: true,
              result: { stream: true, ...push },
            });
          };

          const dispatchResult = await dispatchWorkspaceWidgetsRequest(
            bus,
            cmd.request,
            {
              agentId: cmd.agentId,
              userId: cmd.userId,
              actor: cmd.actor ?? null,
              streamPush,
            },
          );

          // For `subscribe` we keep the handle alive — closing it
          // is the receiver's responsibility on unmount.
          if (dispatchResult.tool === 'workspace_widgets_subscribe') {
            subscribeHandlesRef.current.set(cmd.requestId, dispatchResult.handle);
            client.sendWorkspaceBusReply({
              workspaceId,
              requestId: cmd.requestId,
              ok: true,
              // We can't send a function across the wire, so the
              // server-side stream handle gets a stable subscription
              // id the agent can pass to a future `close` call.
              result: { subscribed: true, subscriptionId: cmd.requestId },
            });
            return;
          }

          // For everything else, the bus's value is the agent's
          // result. We unwrap our discriminator so the LLM sees
          // `result: <whatever the widget returned>` without the
          // bookkeeping wrapper.
          const value =
            'widgets' in dispatchResult
              ? dispatchResult.widgets
              : 'widget' in dispatchResult
                ? dispatchResult.widget
                : dispatchResult.tool === 'workspace_widgets_logs'
                  ? { logs: dispatchResult.logs, loadState: dispatchResult.loadState }
                  : 'result' in dispatchResult
                    ? dispatchResult.result
                    : undefined;
          client.sendWorkspaceBusReply({
            workspaceId,
            requestId: cmd.requestId,
            ok: true,
            result: value,
          });
        } catch (err) {
          const code =
            err instanceof WorkspaceWidgetsError
              ? err.code
              : err instanceof Error &&
                  'code' in err &&
                  typeof (err as { code?: unknown }).code === 'string'
                ? ((err as { code: string }).code as string)
                : 'workspace-widgets/internal';
          let message = err instanceof Error ? err.message : 'dispatch failed';

          // A canvas permission denial is said in app words before anything
          // else (the e2e visual pass of 2026-10-06, see AGENT_CONTROL_*): the
          // bus's "Widget does not allow ..." never reaches the model there.
          if (isAgentPermissionDenial(code) && !bus.isShareSurface) {
            message = AGENT_CONTROL_DENIED;
          }

          // A `permission` denial means this agent is not on the app's
          // `agentControl` allow-list. Turn it into a QUESTION rather than a
          // dead end: raise the same consent screen `files` uses, and tell the
          // agent to wait rather than retry. Deduped server-side per
          // (item, agent), so a retry loop can't spam the bell.
          if (
            isAgentPermissionDenial(code) &&
            cmd.agentId &&
            cmd.request.tool !== 'workspace_widgets_list'
          ) {
            const itemId =
              'itemId' in cmd.request ? cmd.request.itemId : null;
            if (itemId) {
              const asked = await requestAgentControl({
                workspaceId,
                itemId,
                agentId: cmd.agentId,
                agentName: cmd.actor?.name ?? null,
                widgetName: bus.describe(itemId)?.title ?? null,
              });
              /* ALREADY GRANTED (the review of 2026-10-08): the person said
                 yes for this app before. The grant is appended here (the
                 route's event lands too, idempotently), the bus admits the
                 agent for this page at once, and the command runs once more
                 so the agent gets its answer instead of a question that was
                 never raised. A subscribe is not retried: its handle would
                 have to be re-parked, and it is not what a planner asks. */
              if (
                asked === 'granted' &&
                cmd.request.tool !== 'workspace_widgets_subscribe'
              ) {
                appendGrant(itemId, cmd.agentId);
                bus.admit(itemId, cmd.agentId);
                try {
                  const again = await dispatchWorkspaceWidgetsRequest(bus, cmd.request, {
                    agentId: cmd.agentId,
                    userId: cmd.userId,
                    actor: cmd.actor ?? null,
                    // Never reached for a subscribe (excluded above); the same
                    // frame shape as the first run's push, for completeness.
                    streamPush: (push: { itemId: string; eventName: string; payload: unknown }) => {
                      client.sendWorkspaceBusReply({ workspaceId, requestId: cmd.requestId, ok: true, result: { stream: true, ...push } });
                    },
                  });
                  const value =
                    'widgets' in again
                      ? again.widgets
                      : 'widget' in again
                        ? again.widget
                        : again.tool === 'workspace_widgets_logs'
                          ? { logs: again.logs, loadState: again.loadState }
                          : 'result' in again
                            ? again.result
                            : undefined;
                  client.sendWorkspaceBusReply({ workspaceId, requestId: cmd.requestId, ok: true, result: value });
                  return;
                } catch (retryErr) {
                  message = retryErr instanceof Error ? retryErr.message : 'dispatch failed';
                }
              }
              /* A shared link (`/w/`) has no settings and no Agent control:
                 there the bus's words already say what to do, the person's
                 own answer included when they said Not now (the round 8
                 leftover, 2026-09-29; widget-operate-consent.tsx). */
              message = asked === 'asked'
                ? AGENT_CONTROL_ASKED
                : asked === 'granted'
                  ? message
                  : bus.isShareSurface
                    ? message
                    : AGENT_CONTROL_NOT_ASKED;
            }
          }

          client.sendWorkspaceBusReply({
            workspaceId,
            requestId: cmd.requestId,
            ok: false,
            error: { code, message },
          });
        }
      })();
    },
  );

  // Tear down all live subscribes on unmount. We don't tear down
  // per-frame because the bus already does that when the widget
  // unregisters; this is the safety net for tab close / workspace
  // switch.
  useEffect(() => {
    const handles = subscribeHandlesRef.current;
    return () => {
      for (const [, h] of handles) {
        try {
          h.close();
        } catch {
          // ignore
        }
      }
      handles.clear();
    };
  }, []);

  return null;
}
